import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMessageAttachmentContentBlob } from "../../api/message-attachments-api.js";

/**
 * Same-origin blob URL from the authenticated `/content` endpoint.
 * Presigned R2 URLs are not used as `<audio src>` — cross-origin Range
 * requests and `display:none` audio are the usual play() failure modes.
 */
export function useAuthorizedAudioSrc(
  organizationId: number,
  attachmentId: string,
  enabled: boolean,
): {
  playbackSrc: string | undefined;
  sourceFailed: boolean;
} {
  const blobUrlRef = useRef<string | null>(null);
  const blobPromiseRef = useRef<Promise<string | null> | null>(null);
  const [playbackSrc, setPlaybackSrc] = useState<string | undefined>(undefined);
  const [sourceFailed, setSourceFailed] = useState(false);

  const resolve = useCallback(async (): Promise<string | null> => {
    if (blobUrlRef.current) {
      return blobUrlRef.current;
    }
    if (blobPromiseRef.current) {
      return blobPromiseRef.current;
    }
    blobPromiseRef.current = fetchMessageAttachmentContentBlob(organizationId, attachmentId)
      .then((blob) => {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        const objectUrl = URL.createObjectURL(blob);
        blobUrlRef.current = objectUrl;
        setPlaybackSrc(objectUrl);
        setSourceFailed(false);
        return objectUrl;
      })
      .catch(() => {
        setSourceFailed(true);
        return null;
      })
      .finally(() => {
        blobPromiseRef.current = null;
      });
    return blobPromiseRef.current;
  }, [attachmentId, organizationId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void resolve();
  }, [enabled, resolve]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  return { playbackSrc, sourceFailed };
}
